import React from 'react';
import { ApexOptions } from 'apexcharts';
import Chart from 'react-apexcharts';
import styled, { useTheme } from 'styled-components';
import { formatAmount } from 'utils/common';
import { useTranslation } from 'react-i18next';
import { CommonMessages } from 'locales/CommonMessage/messages';
interface PieChartProps {
  title: string;
  value1?: number;
  value2?: number;
  totalAmount?: number;
  LengendLabel1: string;
  LengendLabel2: string;
  colors?: string[];
}

const PieChart: React.FC<PieChartProps> = ({
  title,
  value1,
  value2,
  totalAmount,
  LengendLabel1,
  LengendLabel2,
  colors = [],
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const chartOptions: ApexOptions = {
    series: [value1!, value2!],
    chart: {
      type: 'pie',
      width: '100%', // Adjust width dynamically
      toolbar: { show: false }, // Disable extra tools
      events: {
        dataPointSelection: () => {}, // Prevent activation on click
      },
    },
    labels: [LengendLabel1, LengendLabel2],
    colors: colors,
    legend: {
      show: false,
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: (val: number) =>
          `${
            val % 1 === 0
              ? val.toLocaleString()
              : val.toLocaleString(undefined, {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 2,
                })
          } AED`,
      },
    },
    dataLabels: {
      formatter: (val: number, opts) => {
        const value = opts.w.globals.series[opts.seriesIndex];

        // Format numbers to compact notation (e.g., 2K, 1M)
        return new Intl.NumberFormat('en', {
          notation: 'compact',
          compactDisplay: 'short',
        }).format(value);
      },
      offsetY: 10, // Center vertically
      offsetX: 10, // Center horizontally
      style: {
        fontSize: '12px',
        fontFamily: 'Open Sans',
        colors: [theme.white], // Ensure labels are readable
      },
    },
    plotOptions: {
      pie: {
        expandOnClick: false, // Disable slice expansion on click
        donut: { size: '85%' }, // Adjust size for responsiveness
      },
    },
    stroke: {
      show: false, // Remove inner borders
    },
    states: {
      hover: {
        filter: {
          type: 'none', // Disable hover highlighting
        },
      },
      active: {
        filter: {
          type: 'none', // Disable active state highlighting
        },
      },
    },
    responsive: [
      {
        breakpoint: 1600,
        options: {
          chart: {
            height: '174',
          },
        },
      },
      {
        breakpoint: 1024,
        options: {
          chart: {
            height: '174',
          },
        },
      },

      {
        breakpoint: 768, // Mobile breakpoint
        options: {
          chart: {
            height: '174',
          },
          dataLabels: {
            enabled: true, // Ensure values are visible on mobile
            style: {
              fontSize: '10px', // Adjust for smaller screens
            },
          },
        },
      },
      {
        breakpoint: 480,
        options: {
          chart: {
            height: '174',
          },
        },
      },
      {
        breakpoint: 389,
        options: {
          chart: {
            height: '174',
          },
        },
      },
    ],
  };

  return (
    <PieChartContainer className="piechart-container">
      <HeadingWrapper className="heading-wrapper">
        <Title className="title">{title}</Title>
        <Amount className="amount">
          {totalAmount ? formatAmount(totalAmount) : 0} AED
        </Amount>
      </HeadingWrapper>
      {totalAmount !== 0 ? (
        <>
          <ChartWrapper>
            {value1 && value2 && (
              <Chart
                options={chartOptions}
                series={[value1!, value2!]}
                type="pie"
              />
            )}
          </ChartWrapper>
          <LegendContainer className="legend-container">
            <LegendItem className="legend-item">
              <span
                className="color-box"
                style={{ backgroundColor: colors[0] }}
              ></span>
              {LengendLabel1}
            </LegendItem>
            <LegendItem className="legend-item">
              <span
                className="color-box"
                style={{ backgroundColor: colors[1] }}
              ></span>
              {LengendLabel2}
            </LegendItem>
          </LegendContainer>
        </>
      ) : (
        <div className="no-data-found">{t(CommonMessages.noDataFound())}</div>
      )}
    </PieChartContainer>
  );
};

export default PieChart;
const PieChartContainer = styled.div`
  width: 100%;
  max-width: 267px;
  height: 313.54px;
  background: ${p => p.theme.white};
  border-radius: 8px;
  border: 1px solid ${p => p.theme.pieChartBorderClr};
  box-shadow: 0px 3.5px 5.5px 0px ${p => p.theme.pieChartBoxShadowClr};
  margin: 0 0;
  padding: 20px;

  @media (min-width: 300px) and (max-width: 389px) {
    width: 200px;
    height: 250px;
    padding: 10px;
  }

  @media (min-width: 390px) and (max-width: 480px) {
    width: 220px;
    height: 270px;
    padding: 15px;
  }

  @media (min-width: 481px) and (max-width: 768px) {
    width: 240px;
    height: 290px;
    padding: 15px;
  }

  @media (min-width: 769px) and (max-width: 991px) {
    width: 250px;
    height: 300px;
    padding: 20px;
  }

  @media (min-width: 992px) and (max-width: 1023px) {
    width: 260px;
    height: 310px;
    padding: 20px;
  }

  @media (min-width: 1024px) and (max-width: 1279px) {
    width: 267px;
    height: 313.54px;
    padding: 20px;
  }

  @media (min-width: 1280px) and (max-width: 1365px) {
    width: 267px;
    height: 313.54px;
    padding: 20px;
  }

  @media (min-width: 1366px) and (max-width: 1439px) {
    width: 267px;
    height: 313.54px;
    padding: 20px;
  }

  @media (min-width: 1440px) and (max-width: 1600px) {
    width: 267px;
    height: 313.54px;
    padding: 20px;
  }

  .no-data-found {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 205px;
    font-size: 12px;
  }
`;
const HeadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0 10px 0;
`;
const Title = styled.div`
  font-family: 'Open Sans';
  font-size: 12px;
  font-weight: 600;
  line-height: 16.8px;
  text-align: left;
  color: ${p => p.theme.pieChartLableClr};

  @media (min-width: 300px) and (max-width: 389px) {
    font-size: 10px;
    line-height: 14px;
  }

  @media (min-width: 390px) and (max-width: 480px) {
    font-size: 11px;
    line-height: 15.4px;
  }

  @media (min-width: 481px) and (max-width: 768px) {
    font-size: 12px;
    line-height: 16.8px;
  }
`;

const Amount = styled.div`
  font-family: 'Open Sans';
  font-size: 12px;
  font-weight: 600;
  line-height: 16.8px;
  text-align: left;
  color: ${p => p.theme.pieChartLableClr};

  @media (min-width: 300px) and (max-width: 389px) {
    font-size: 10px;
    line-height: 14px;
  }

  @media (min-width: 390px) and (max-width: 480px) {
    font-size: 11px;
    line-height: 15.4px;
  }

  @media (min-width: 481px) and (max-width: 768px) {
    font-size: 12px;
    line-height: 16.8px;
  }
`;

const ChartWrapper = styled.div`
  width: 100%;
`;

const LegendContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 34px;
`;

const LegendItem = styled.span`
  display: flex;
  align-items: center;
  margin-right: 15px;
  font-size: 12px;
  color: ${p => p.theme.black};

  &:last-child {
    margin-right: 0;
  }

  .color-box {
    border-radius: 50%;
    width: 10px;
    height: 10px;
    margin-right: 5px;
  }
`;
